import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-pgt-p"),
  kind: "test",
  tags: ["testing", "both-partners", "genetic-screening", "ivf", "genomic"],
  title: "PGT-P (Polygenic Risk Scoring)",
  subtitle: "Estimates embryo risk for some complex conditions",
  icon: "🧬",
  body: {
    testName: "Preimplantation Genetic Testing for Polygenic Risk (PGT-P)",
    category: "genetic",
    parentTest: {
      testId: makeId("test-preimplantation-genetic-testing"),
      badgeLabel: "View all PGT tests",
    },
    evidenceStrength: "emerging",
    whatItMeasures:
      "PGT-P uses polygenic risk scores to estimate an embryo's relative genetic predisposition for some complex conditions, such as coronary artery disease, diabetes, or certain cancers.\n\nIt is probabilistic risk information, not a diagnosis.\n\nIn modern embryo-genomics workflows, PGT-P may be bundled with PGT-A or built from PGT-A-derived embryo data plus parental genome sequencing, so patients should ask what is already included in the report.",
    howItWorks:
      "PGT-P usually starts from embryo biopsy DNA during IVF, often after or alongside PGT-A. Some whole-genome embryo platforms generate PGT-A, monogenic findings, structural rearrangement information, and polygenic scores from the same biopsy. Other approaches use the ultra-low-pass sequence data already generated for routine PGT-A, then combine it with parental genome sequencing to impute more of the embryo genome. The report may rank embryos by predicted risk, but the important first question is whether the patient is already getting PGT-A and broader analysis from the same sample.",
    howItWorksSteps: [
      "Embryos are biopsied during IVF, usually at the blastocyst stage.",
      "The lab either sequences the biopsy more broadly or uses data already generated for PGT-A together with parental genomes.",
      "The report should clarify whether PGT-A-style aneuploidy screening is included, because many broader workflows already include it.",
      "A polygenic model estimates relative or absolute risk for selected complex conditions.",
      "The report may compare embryos against each other after chromosome status, embryo quality, and any known monogenic risks have already been considered.",
      "A genetics professional should explain which layers came from the same biopsy and what each layer can add.",
    ],
    quickFacts: [
      { kind: "sample", value: "Embryo biopsy plus parental DNA" },
      { kind: "time", value: "No extra patient procedure" },
      { kind: "pain", value: "None for patient" },
      { kind: "anesthesia", value: "None for the biopsy" },
      { kind: "prep", value: "Requires IVF and enough embryos to compare" },
      { kind: "results", value: "Depends on lab workflow" },
    ],
    resultTypes: [
      {
        label: "Lower predicted risk",
        meaning:
          "The embryo has a lower estimated polygenic risk score for one or more reported conditions compared with the comparison group or other embryos.",
        whatToExpect:
          "This does not mean the child will avoid the condition. Environment, family history, ancestry, chance, and non-polygenic factors still matter.",
      },
      {
        label: "Higher predicted risk",
        meaning:
          "The embryo has a higher estimated polygenic risk score for one or more reported conditions.",
        whatToExpect:
          "This does not mean the child will develop the condition. The result should not be interpreted like a single-gene disease result.",
      },
      {
        label: "Limited-use score",
        meaning:
          "The model may be less informative because of ancestry, small embryo number, uncertain disease model, or missing family context.",
        whatToExpect:
          "A genetics professional should explain whether the result is meaningful enough to use in transfer decisions.",
      },
      {
        label: "No-result",
        meaning: "The lab cannot generate a reliable risk score.",
        whatToExpect:
          "Your clinic may ignore the PGT-P layer and rely on embryo quality, PGT-A if performed, and your original transfer plan.",
      },
    ],
    expectations: {
      canExpect: [
        "A probabilistic score or ranking for selected complex conditions",
        "In some workflows, PGT-A plus broader embryo genomic information from the same biopsy",
        "Potentially more context when several embryos are otherwise similar",
        "A fast-moving evidence base that may change as studies become more diverse and longitudinal",
      ],
      shouldNotExpect: [
        "ASRM Ethics Committee 2026 recommends against offering PGT-P as a clinical service at this time - it is considered nascent, unproven, and inappropriate for routine clinical use",
        "ESHRE and major European societies similarly do not endorse polygenic embryo screening outside of research",
        "PGT-P is illegal in France, Germany, the Netherlands, and several other European countries - polygenic embryo selection is not legally accessible there",
        "A diagnosis, a guarantee of health, or a way to eliminate disease risk",
        "The same clinical certainty as PGT-M for a known high-risk single-gene condition",
        "Every clinic to understand or explain the technology equally well; ask the lab what data were generated and what is included",
      ],
    },
    considerations: [
      "Ask whether the broader report already includes PGT-A, so you do not pay twice for aneuploidy screening.",
      "Ask whether the lab is doing whole-genome embryo sequencing or deriving the embryo genome from PGT-A data plus parental genomes.",
      "If the clinic mentions Orchid, Herasight, or a similar platform, ask whether PGT-A is included, reused, or still billed separately.",
      "Ask whether the same biopsy can return additional useful information; once biopsy is already happening, cost and lab access are usually the main reasons to limit the report.",
      "Ask how the clinic would use the information if embryo quality, PGT-A, PGT-M, and PGT-P point in different directions.",
      "Ask for genetic counseling that explains absolute risk, relative risk, residual risk, and how the report should affect transfer order.",
    ],
    bestFor: [
      "People already doing IVF with embryo biopsy who want the most information available from the same sample",
      "People using a broader embryo-genomics platform that includes PGT-A and additional analysis in one workflow",
      "People with multiple embryos to compare who want to understand emerging polygenic risk information",
      "Not a substitute for PGT-M when a known familial single-gene risk exists",
      "Most limited when cost is prohibitive or when there is only one embryo and no transfer decision to rank",
    ],
    keyFindings: [
      {
        direction: "neutral",
        outcome:
          "PGT-P can estimate relative genetic predisposition for some complex conditions, but the result is probabilistic",
        mechanism:
          "Complex diseases are influenced by many genetic variants plus environment, lifestyle, ancestry, age, and chance.",
        source: "ASRM PGT-P ethics committee opinion, 2026",
      },
      {
        direction: "negative",
        outcome:
          "ASRM Ethics Committee 2026 recommends against offering PGT-P as a clinical service at this time - it is considered nascent and unproven",
        mechanism:
          "Polygenic risk scores have been validated mostly in European-ancestry populations, have limited predictive value at the individual level, and have not been shown to improve embryo-selection outcomes in any RCT.",
        source: "ASRM PGT-P ethics committee opinion, 2026",
      },
      {
        direction: "neutral",
        outcome:
          "ACMG describes polygenic risk scores as screening tools rather than diagnostic tests",
        mechanism:
          "A lower score does not rule out disease and a higher score does not mean the disease will happen.",
        source: "ACMG points to consider statement, 2024",
      },
      {
        direction: "neutral",
        outcome:
          "The potential value is most plausible when there are multiple otherwise transferable embryos and the condition model is clinically meaningful",
        mechanism:
          "Embryo numbers are often limited, and ranking only helps if there are real alternatives to choose from.",
        source: "Human Reproduction Update review, 2024",
      },
    ],
    headlineStat: "Emerging",
    turnaroundTime: "Depends on lab workflow",
    atHome: false,
    clinicRequired: false,
    dataPoints: [],
    section: "genetic-screening",
  },
  sources: [
    {
      title:
        "Use of preimplantation genetic testing for polygenic disorders (PGT-P): an Ethics Committee opinion",
      authors:
        "Ethics Committee of the American Society for Reproductive Medicine",
      journal: "Fertility and Sterility",
      year: 2026,
      url: "https://www.asrm.org/practice-guidance/ethics-opinions/use-of-preimplantation-genetic-testing-for-polygenic-disorders-pgt-p-an-ethics-committee-opinion-2026/",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Clinical utility of polygenic risk scores for embryo selection: a points-to-consider statement",
      authors: "Grebe TA et al. (ACMG)",
      journal: "Genetics in Medicine",
      year: 2024,
      url: "https://pubmed.ncbi.nlm.nih.gov/38393332/",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Screening embryos for polygenic disease risk: a review of epidemiological, clinical, and ethical considerations",
      authors: "Pagnaer T, Siermann M, Borry P, Tšuiko O",
      journal: "Human Reproduction Update",
      year: 2024,
      url: "https://academic.oup.com/humupd/article/30/5/529/7684172",
      evidenceLevel: "review",
    },
  ],
  readTimeMinutes: 5,
};

registerBlock(test);
